/* 🏁 Final Performance Review (DESIGN-endgame-review.md, first slice).
 *
 * Appears as a tab once the game is finished (the engine's final news item
 * already points students here). Composite ring + letter grade, final market
 * standing vs the AI rivals, the 8-dimension report card with sparklines and
 * per-dimension feedback, journey highlights, financial summary, and
 * reflection prompts that feed the strategy journal.
 *
 * Everything renders from data the client already has: `state.history` holds
 * the engine's full per-round result objects (sc, composite, share, …) and
 * `snapshots` the financial series. Restart flow is deferred (needs backend
 * `allowRestart` support per the design doc §3).
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, Badge } from '../../components/ui';
import { money, pct } from '../../lib/format';
import { STRATEGIES, type Named } from '../../lib/constants';
import type { ScreenProps } from './types';

const DIMS: Array<{ key: string; label: string; icon: string }> = [
  { key: 'profitability', label: 'Profitability', icon: '💰' },
  { key: 'share', label: 'Market share', icon: '📈' },
  { key: 'brand', label: 'Brand', icon: '✨' },
  { key: 'quality', label: 'Quality', icon: '🛠️' },
  { key: 'satisfaction', label: 'Satisfaction', icon: '😊' },
  { key: 'operations', label: 'Operations', icon: '🏭' },
  { key: 'innovation', label: 'Innovation', icon: '🔬' },
  { key: 'finance', label: 'Financial health', icon: '🏦' },
];

const gradeOf = (c: number) => (c >= 85 ? 'A' : c >= 70 ? 'B' : c >= 55 ? 'C' : c >= 40 ? 'D' : 'F');
const GRADE_TONE: Record<string, string> = { A: 'text-good', B: 'text-good', C: 'text-warn', D: 'text-warn', F: 'text-bad' };

const DIM_FEEDBACK: Record<string, { high: string; low: string }> = {
  profitability: { high: 'Margins and cumulative profit carried the scorecard.', low: 'Losses or thin margins dragged the score — revisit pricing vs unit cost and the fixed-cost base.' },
  share: { high: 'You claimed real market share and held it.', low: 'Demand went to rivals — coverage (distribution) and rating (price/quality/brand fit) decide share.' },
  brand: { high: 'Brand equity compounded quarter after quarter.', low: 'Brand never built momentum — consistent, well-targeted campaigns beat sporadic spend.' },
  quality: { high: 'Component choices kept perceived quality high.', low: 'Quality lagged the segment’s expectations — better parts cost more but rate higher.' },
  satisfaction: { high: 'Customers got what they paid for, and came back.', low: 'Stockouts or price/value mismatch hurt satisfaction.' },
  operations: { high: 'Supply met demand with little waste.', low: 'Stockouts or inventory pile-ups — the newsvendor trade-off wants another look.' },
  innovation: { high: 'R&D depth paid off in better products.', low: 'Little research completed — rivals out-teched you over the long game.' },
  finance: { high: 'A healthy capital structure: neither reckless nor idle.', low: 'Leverage, emergency credit, or idle hoarding weighed on financial health.' },
};

export function FinalReview({ detail }: ScreenProps) {
  const s = detail.state as any;
  const history = (s.history ?? []) as any[];
  const last = history.at(-1) ?? {};
  const composite = Math.round(last.composite ?? detail.game.composite ?? 0);
  const grade = gradeOf(composite);
  const sc = (last.sc ?? {}) as Record<string, number>;
  const strategy = (STRATEGIES as Named[]).find((x) => x.id === detail.game.strategy);

  // Final market standing: own share + rival shares from the last result.
  const rivals = Object.entries((last.compShare ?? {}) as Record<string, number>)
    .map(([id, share]) => ({ id, name: (s.comps ?? []).find((c: any) => c.id === id)?.name ?? id, share: share as number }));
  const standing = [...rivals, { id: 'you', name: `${detail.game.companyName} (you)`, share: last.share ?? 0 }]
    .sort((a, b) => b.share - a.share);
  const rank = standing.findIndex((x) => x.id === 'you') + 1;

  // Journey highlights from the snapshots series.
  const snaps = detail.snapshots;
  const best = [...snaps].sort((a, b) => (b.profit ?? -Infinity) - (a.profit ?? -Infinity))[0];
  const worst = [...snaps].sort((a, b) => (a.profit ?? Infinity) - (b.profit ?? Infinity))[0];
  const cumRevenue = snaps.reduce((a, x) => a + (x.revenue ?? 0), 0);
  const cumProfit = (last.cumProfit as number | undefined) ?? snaps.reduce((a, x) => a + (x.profit ?? 0), 0);
  // Quarters spent on insolvency watch / drawing the overdraft (rules v2 records both per quarter).
  const insolventQ = history.filter((h) => h.insolvent).length;
  const overdraftQ = history.filter((h) => h.revolverUsedQ).length;

  const dimSeries = (key: string) => history.map((h) => (h.sc?.[key] as number | undefined) ?? null);
  const ranked = DIMS.map((d) => ({ ...d, v: Math.round(sc[d.key] ?? 0) })).sort((a, b) => b.v - a.v);
  // DIMS is a fixed non-empty list, so first/last always exist.
  const bestDim = ranked[0]!, worstDim = ranked[ranked.length - 1]!;

  return (
    <div className="space-y-5">
      {/* Hero: the verdict */}
      <Card className="flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          <div className="grid h-28 w-28 place-items-center rounded-full border-4"
            style={{ borderColor: grade === 'F' || grade === 'D' ? 'var(--bad)' : 'var(--acc)' }}>
            <div className="text-center">
              <div className="text-3xl font-extrabold">{composite}</div>
              <div className={`text-lg font-bold ${GRADE_TONE[grade]}`}>{grade}</div>
            </div>
          </div>
          <div>
            <h2 className="text-xl font-bold">🏁 Final Performance Review</h2>
            <p className="mt-1 max-w-md text-sm text-mut">
              {detail.game.maxRounds} quarters complete. Final composite score {composite}/100 —
              {' '}#{rank} of {standing.length} in worldwide market standing.
            </p>
          </div>
        </div>
        <div className="text-sm text-mut">
          <div>Strategy declared: <b className="text-txt">{(strategy?.icon as string) ?? ''} {(strategy?.name as string) ?? detail.game.strategy}</b></div>
          <div className="mt-1">Final cash <b className="text-txt">{money(s.cash)}</b> · brand <b className="text-txt">{Math.round(s.brand ?? 0)}</b></div>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Market standing */}
        <Card>
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-mut">Final market standing</h3>
          <ul className="space-y-2 text-sm">
            {standing.map((x, i) => (
              <li key={x.id} className={`flex items-center justify-between rounded-sm px-2 py-1.5 ${x.id === 'you' ? 'bg-acc/10 font-semibold' : ''}`}>
                <span>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`} {x.name}</span>
                <span className="text-mut">{pct(x.share)}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* Journey + financials */}
        <Card>
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-mut">The journey</h3>
          <ul className="space-y-1.5 text-sm text-mut">
            <li>Cumulative revenue <b className="text-txt">{money(cumRevenue)}</b> · cumulative profit <b className={cumProfit >= 0 ? 'text-good' : 'text-bad'}>{money(cumProfit)}</b></li>
            {best && <li>Best quarter: <b className="text-txt">Q{best.round}</b> ({money(best.profit)} profit)</li>}
            {worst && <li>Toughest quarter: <b className="text-txt">Q{worst.round}</b> ({money(worst.profit)} profit)</li>}
            <li>Products shipped: <b className="text-txt">{(s.products ?? []).length}</b> · technologies researched: <b className="text-txt">{s.rnd?.done?.length ?? 0}</b></li>
            {(insolventQ > 0 || overdraftQ > 0) && (
              <li>
                Financial distress: {overdraftQ > 0 && <>drew the emergency overdraft in <b className="text-bad">{overdraftQ}</b> quarter{overdraftQ === 1 ? '' : 's'}</>}
                {overdraftQ > 0 && insolventQ > 0 && ' · '}
                {insolventQ > 0 && <>spent <b className="text-bad">{insolventQ}</b> quarter{insolventQ === 1 ? '' : 's'} on insolvency watch{s.insolvent ? ' (still on it at the close)' : ''}</>}
              </li>
            )}
          </ul>
        </Card>
      </div>

      {/* Report card */}
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-mut">Report card — eight dimensions</h3>
          <div className="flex gap-2">
            <Badge tone="good">Strongest: {bestDim.label}</Badge>
            <Badge tone="bad">Weakest: {worstDim.label}</Badge>
          </div>
        </div>
        <div className="space-y-2.5">
          {ranked.map((d) => {
            const series = dimSeries(d.key).filter((v): v is number => v != null);
            const fb = DIM_FEEDBACK[d.key];
            return (
              <div key={d.key} className="grid grid-cols-[150px_1fr_44px] items-center gap-3 text-sm">
                <span className="text-mut">{d.icon} {d.label}</span>
                <div>
                  <div className="h-2 overflow-hidden rounded-full bg-[color:var(--track)]">
                    <div className="h-full rounded-full" style={{ width: `${d.v}%`, background: d.v >= 55 ? 'var(--good)' : d.v >= 40 ? 'var(--warn)' : 'var(--bad)' }} />
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-dim">
                    <span className="font-mono">{series.map((v) => '▁▂▃▄▅▆▇█'[Math.min(7, Math.floor((v / 100) * 8))]).join('')}</span>
                    <span>{d.v >= 55 ? fb?.high : fb?.low}</span>
                  </div>
                </div>
                <b className="text-right">{d.v}</b>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Reflection */}
      <Card>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-mut">Debrief — for your final journal</h3>
        <ol className="ml-5 list-decimal space-y-1.5 text-sm text-mut">
          <li>Did you execute the strategy you declared ({(strategy?.name as string) ?? detail.game.strategy}) — or did you drift? Point to two quarters as evidence.</li>
          <li>Your weakest dimension was <b className="text-txt">{worstDim.label}</b>. Which specific decision would you change first, and what theory from the Academy explains why?</li>
          <li>Which rival hurt you most, and what would you do differently against them in the first two quarters?</li>
        </ol>
        <p className="mt-3 text-xs text-dim">Write your answers in the strategy journal on the Overview tab — your instructor reads them alongside this review.</p>
      </Card>
    </div>
  );
}
